import {
  BehaviorSubject,
  defer,
  distinctUntilChanged,
  Observable,
  retryWhen,
  shareReplay, Subject,
  switchMap,
  tap,
  throwError
} from 'rxjs';
import { catchError } from 'rxjs/operators';
import { UserStateService } from './user-state.class';

export enum ConnectionStatus {
  online,
  offline
}

export class RequestWrapperClass {
  // @ts-ignore
  readonly connectionStatusSubj$: BehaviorSubject<ConnectionStatus> = new BehaviorSubject(ConnectionStatus.online);
  readonly connectionStatus$ = this.connectionStatusSubj$.pipe(
    distinctUntilChanged(),
    shareReplay(),
  );
  readonly retryCalls$: Subject<void> = new Subject();

  constructor(public userStateService: UserStateService) {
  }

  isNetworkError(response?: { code?: string; errors?: { message: string; extensions?: { code?: string } }[] }): boolean {
    // Check for explicit network error code
    if (response?.code === 'NetworkError') {
      console.log('Network Error: Explicit NetworkError code detected');
      return true;
    }
    
    // Check for network error messages, but exclude GraphQL validation errors
    const hasNetworkError = (response?.errors || []).some(e => {
      // If it's a GraphQL validation error, don't treat it as a network error
      if (e.extensions?.code === 'GRAPHQL_VALIDATION_FAILED') {
        console.log('GraphQL Validation Error (not network error):', e.message);
        return false;
      }
      
      // Only treat as network error if it's an actual network-related message
      const isNetworkRelated = e.message === 'Network Error' || 
                              e.message.toLowerCase().includes('network') ||
                              e.message.toLowerCase().includes('connection');
      
      if (isNetworkRelated) {
        console.log('Network Error:', e.message);
      }
      
      return isNetworkRelated;
    });
    
    // Log other types of errors for debugging (but don't treat as network errors)
    if (!hasNetworkError && response?.errors && response.errors.length > 0) {
      const errorTypes = response.errors.map(e => e.extensions?.code || 'UNKNOWN').join(', ');
      console.log(`Non-network error detected (${errorTypes}):`, response.errors[0].message);
    }
    
    return hasNetworkError;
  }

  public retry() {
    this.retryCalls$.next();
  }

  public getConnectionStatus(): Observable<ConnectionStatus> {
    return this.connectionStatus$;
  }

  public createRetryableObserver<T>(promiseFactory: () => Promise<T>): Observable<T> {
    return defer(promiseFactory).pipe(
      retryWhen(errors =>
        errors.pipe(
          switchMap(error => {
            if (this.isNetworkError(error)) {
              this.connectionStatusSubj$.next(ConnectionStatus.offline);

              if (this.userStateService.isNetworkAvailable.value == true) {
                this.userStateService.isNetworkAvailable.next(false);
              }

              console.error('Connection error', error);
              return this.retryCalls$;
            }
            return throwError(error);
          }),
        )
      ),
      catchError(error => {
        //TODO Check it
        this.connectionStatusSubj$.next(ConnectionStatus.online);

        if (this.userStateService.isNetworkAvailable.value == false) {
          this.userStateService.isNetworkAvailable.next(true);
        }

        throw error;
      }),
      tap(() => {
        this.connectionStatusSubj$.next(ConnectionStatus.online);
        if (this.userStateService.isNetworkAvailable.value == false) {
          this.userStateService.isNetworkAvailable.next(true);
        }
      }),
    );
  }

  checkResponseData(res: object) {
    if (res === null) {
      this.userStateService.isDataAvailable.next(false);
    } else {

      if (this.userStateService.isDataAvailable.value == false) {
        this.userStateService.isDataAvailable.next(true);
      }
    }
  }
}
